import React from 'react';
import PropTypes from 'prop-types';

import RenderImage from '../utils/RenderImage';
import formatString from '../../utils/formatString';

const isYesNo = (v) => typeof v === 'string' && /^(s[ií]|no|yes)$/i.test(v);
const isPositive = (v) => (typeof v === 'boolean' ? v : /^(s[ií]|yes)$/i.test(String(v)));

const AnswerField = ({ value }) => {
	if (typeof value === 'boolean' || isYesNo(value)) {
		const positive = isPositive(value);
		return (
			<div className={`pn-qa-pill ${positive ? 'pos' : 'neg'}`}>
				<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
					{positive
						? <path d='M5 12l5 5L20 7' />
						: <path d='M6 6l12 12M18 6 6 18' />
					}
				</svg>
				{typeof value === 'boolean' ? (value ? 'Sí' : 'No') : String(value)}
			</div>
		);
	}
	if (typeof value === 'number') {
		return <div className='pn-qa-num'>{value.toLocaleString('en-US', { maximumFractionDigits: 2 })}</div>;
	}
	if (typeof value === 'string' && value.length > 60) {
		return <div className='pn-qa-long'>{value}</div>;
	}
	return <div className='pn-qa-pill'>{String(value)}</div>;
};

AnswerField.propTypes = {
	value: PropTypes.any,
};

const AnswerPhoto = ({ imageId, label }) => (
	<div className='pn-qa-photo'>
		<RenderImage
			imageId={imageId}
			defaultSrc={`${process.env.PUBLIC_URL}/assets/ref-image2.svg`}
			alt={`${label} image`}
			refImage='surveys'
		/>
	</div>
);

AnswerPhoto.propTypes = {
	imageId: PropTypes.string,
	label: PropTypes.string,
};

/**
 * Renders one submodule item: its inner value-fields as a small key/value list
 * and its photos grouped by `photoType` (same look as PayloadContentQA's
 * AnswerSection, only the input shape differs — parseSurveyData produces
 * nested items rather than a single section blob).
 */
const SubmoduleItem = ({ item, label }) => {
	const itemFields = Array.isArray(item?.fields) ? item.fields : [];
	const itemPhotos = Array.isArray(item?.photos) ? item.photos : [];

	const valueFields = itemFields.filter((f) => f.type === 'value');
	const imageFields = itemFields.filter((f) => f.type === 'image');

	const groupedByType = {};
	itemPhotos.forEach((p) => {
		const type = p?.photoType ?? '—';
		(groupedByType[type] = groupedByType[type] || []).push(p);
	});
	const types = Object.keys(groupedByType);

	return (
		<div className='pn-qa-section'>
			{valueFields.length > 0 && (
				<div className='pn-qa-section-itemlist'>
					{valueFields.map((f, i) => (
						<div className='pn-qa-section-item' key={i}>
							<span className='pn-qa-section-item-label'>{formatString(f.label)}:</span>
							<span className='pn-qa-section-item-value'>{String(f.value)}</span>
						</div>
					))}
				</div>
			)}

			{imageFields.length > 0 && (
				<div className='pn-qa-section-photos'>
					{imageFields.map((f, i) => (
						<RenderImage
							key={i}
							imageId={f.value}
							defaultSrc={`${process.env.PUBLIC_URL}/assets/ref-image2.svg`}
							alt={`${f.label} image`}
							refImage='surveys'
						/>
					))}
				</div>
			)}

			{types.map((type) => (
				<div className='pn-qa-section-group' key={type}>
					{type !== '—' && (
						<div className='pn-qa-section-group-head'>
							<span className='pn-qa-section-group-dot' />
							<span className='pn-qa-section-group-label'>{type}</span>
						</div>
					)}
					<div className='pn-qa-section-photos'>
						{groupedByType[type].map((p, i) => (
							<RenderImage
								key={i}
								imageId={p?.imageId}
								defaultSrc={`${process.env.PUBLIC_URL}/assets/ref-image2.svg`}
								alt={`${label} ${type} photo`}
								refImage='surveys'
							/>
						))}
					</div>
				</div>
			))}
		</div>
	);
};

SubmoduleItem.propTypes = {
	item: PropTypes.object,
	label: PropTypes.string,
};

/**
 * Survey-flavoured content viewer that matches the visual treatment of
 * `<PayloadContentQA />` (numbered rows, label, pill/photo/section answers).
 * Reads `parseSurveyData()`'s shape directly:
 *   [{ type: 'image' | 'submodule' | 'value', label, value }]
 *
 * Must render inside a wrapper carrying the `.payload-new` class so the
 * `pn-qa-*` styles apply.
 */
const SurveyContent = ({ content }) => {
	const fields = Array.isArray(content) ? content : [];

	if (fields.length === 0) {
		return (
			<p style={{ color: 'var(--ds-muted)', fontSize: 13 }}>
				No content available.
			</p>
		);
	}

	// Each parsed field becomes one row. Submodules expand each of their items
	// into its own numbered row so the user sees them as distinct entries —
	// matches how Payload Content treats repeated sections.
	const rows = [];
	fields.forEach((field) => {
		if (field.type === 'submodule') {
			const items = Array.isArray(field.value) ? field.value : [];
			items.forEach((item, itemIdx) => {
				rows.push({
					kind: 'submodule-item',
					label: items.length > 1
						? `${formatString(field.label)} #${itemIdx + 1}`
						: formatString(field.label),
					rawLabel: field.label,
					value: item,
				});
			});
			return;
		}
		if (field.type === 'image') {
			const imageIds = Array.isArray(field.value) ? field.value : [];
			rows.push({
				kind: 'image',
				label: formatString(field.label),
				rawLabel: field.label,
				value: imageIds,
			});
			return;
		}
		rows.push({
			kind: 'field',
			label: formatString(field.label),
			rawLabel: field.label,
			value: field.value,
		});
	});

	return (
		<div className='pn-content-wrap'>
			{rows.map((r, i) => (
				<div className='pn-qa-row' key={i}>
					<div className='pn-qa-number'>{String(i + 1).padStart(2, '0')}.</div>
					<div className='pn-qa-body'>
						<div className='pn-qa-label'>{r.label}</div>

						{r.kind === 'image' && (
							r.value.length > 0
								? r.value.length === 1
									? <AnswerPhoto imageId={r.value[0]} label={r.rawLabel} />
									: (
										<div className='pn-qa-section-photos'>
											{r.value.map((id, idx) => (
												<RenderImage
													key={idx}
													imageId={id}
													defaultSrc={`${process.env.PUBLIC_URL}/assets/ref-image2.svg`}
													alt={`${r.rawLabel} image`}
													refImage='surveys'
												/>
											))}
										</div>
									)
								: <div className='pn-qa-pill'>—</div>
						)}

						{r.kind === 'submodule-item' && (
							<SubmoduleItem item={r.value} label={r.rawLabel} />
						)}

						{r.kind === 'field' && <AnswerField value={r.value} />}
					</div>
				</div>
			))}
		</div>
	);
};

SurveyContent.propTypes = {
	content: PropTypes.array,
};

export default SurveyContent;
